import { portalText, usePortalTranslation } from "../../../i18n/portal.js";
/**
 * `data-screen-label="Template picker"` — the `cards` variant (product decision A5; the
 * `index` variant is registered as not built in `docs/design/screen-manifest.json`).
 *
 * WHAT THE API CAN AND CANNOT SAY. `GET /v1/templates` is `WHERE available = true`, so the
 * four templates that must appear *disabled* are structurally absent from it. The six-row
 * catalogue therefore comes from `@wetalk/domain`'s `AGENT_TEMPLATE_CATALOGUE` — the same
 * data `0006-p7-factory.sql` seeds — and the API's response decides which of them is
 * buildable. That merge happens in `src/api/api-gateway.ts`; this screen just renders cards.
 *
 * AMENDED 2026-10-09 (design sync, unit B): the kicker and the standfirst gave way to the step list's first mark
 * ("1 Template · Next steps depend on the agent you choose"), and each card says whether the agent answers incoming
 * calls or makes outgoing ones (`directionTagOf`). The four coming-soon templates stay, disabled, as P10-02 decided.
 *
 * AMENDED 2026-10-09 (plan-015 CAT, G7): the tag reads the catalogue's `direction` — stated per template since 0118
 * and served by `GET /v1/templates` — instead of inferring it from the template's fields, so a coming-soon card is
 * tagged too (each states who starts its conversations).
 *
 * AMENDED 2026-10-10 (design sync of the export of 2026-10-10): the line under the description reads "You'll need: your
 * menu" (the catalogue's `you_add`, migration 0132), and the badge is drawn only when it says something — an available
 * card carries none (the export dropped "Available now"); a coming-soon card keeps "Coming soon", because it cannot be
 * chosen and must say so (P10-02, copy rule 2026-09-23).
 *
 * A disabled card is a real `<button disabled>`, not a div with an opacity: it stays in the
 * tab order's logic, announces itself as unavailable, and cannot be clicked by a script.
 */
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Icon, Tag } from "@wetalk/ui";
import type { ReactNode } from "react";

import { glyphOf } from "../../../shell/glyph.js";
import { useGateway } from "../../../api/provider.js";
import { agentTemplateQuery } from "../../../api/query.js";
import type { AgentTemplateCardView } from "../../../api/gateway.js";
import { VoiceAgentAudienceTag, isInternalAgentTemplate } from "../agent-audience.js";
import { directionTagOf } from "./wizard-rules.js";
import { SHARED_CONFIGURATION_QUERY_KEY, type SharedVoiceAgentConfigurationView } from "./shared-configuration-gateway.js";
import "../screen.css";
import "./new-agent.css";

/**
 * plan-014 GAP (B5's hand-off): "Start from a shared configuration". The configurations another workspace of the
 * organization shared and this one accepted, offered beside the templates; choosing one opens the same wizard on its
 * kind of agent with its languages, voice and greeting already filled. Absent where the gateway has none (the
 * sandbox) or nothing is shared — never an empty block.
 */
function SharedConfigurationBlock(props: { readonly catalogue: readonly AgentTemplateCardView[] }): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const navigate = useNavigate();
  const listShared = gateway.listSharedVoiceAgentConfigurations;
  const shared = useQuery({
    queryKey: SHARED_CONFIGURATION_QUERY_KEY,
    queryFn: (): Promise<readonly SharedVoiceAgentConfigurationView[]> => (listShared === undefined ? Promise.resolve([]) : listShared()),
    enabled: listShared !== undefined,
    staleTime: 0,
  });
  const buildable = (shared.data ?? []).flatMap((one) => {
    const card = props.catalogue.find((candidate) => candidate.code === one.agentTemplateCode && candidate.available);
    return card === undefined ? [] : [{ one, card }];
  });
  if (buildable.length === 0) return null;
  return (
    <>
      <h2 className="c-picker-section-title">{portalText("operations", "start_from_a_shared_configuration_2c1c4791")}</h2>
      <p className="c-screen-lede">
        {portalText("operations", "shared_with_this_workspace_by_another_workspace_of_your_or_a10e1680")}</p>
      <div className="c-picker-grid">
        {buildable.map(({ one, card }) => (
          <button
            type="button"
            key={one.resourceShareId}
            className="c-picker-card"
            onClick={() => {
              void navigate({
                to: "/agents/new/$agentTemplateCode",
                params: { agentTemplateCode: card.code },
                search: { step: 2, share: one.resourceShareId },
              });
            }}
          >
            <span className="c-picker-card-head">
              <span className="c-picker-card-mark">
                <Icon glyph={glyphOf(card.iconName)} size="xl" />
              </span>
              <span className="c-picker-card-badge">
                <Tag tone="accent">{portalText("operations", "shared_50d0d8dd")}</Tag>
                <VoiceAgentAudienceTag agentTemplateCode={card.code} />
              </span>
            </span>
            <span className="c-picker-card-name">
              {one.sourceVoiceAgentName}
              {one.sourceAgentVersionLabel === null ? "" : ` · ${one.sourceAgentVersionLabel}`}
            </span>
            <span className="c-picker-card-dek">
              {card.name} {portalText("operations", "from_b9426d12")}{one.sourceWorkspaceName}
            </span>
            <span className="c-picker-card-needs">{portalText("operations", "merge.wizard.you_ll_need.ebadd7")}{card.youAdd}</span>

          </button>
        ))}
      </div>
    </>
  );
}

export function AgentTemplatePickerScreen(): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const navigate = useNavigate();
  const catalogue = useQuery(agentTemplateQuery(gateway));

  if (catalogue.data === undefined) {
    return <p className="c-note">{portalText("operations", "reading_the_catalogue_2a05727e")}</p>;
  }

  return (
    <section className="c-screen">
      {/* Design sync 2026-10-09: the step list starts here — the picker is step 1, and the steps after it depend on the
          template chosen (Menu, Hours and Orders for one with an order book), so it names only this one. */}
      <ol aria-label={portalText("operations", "steps_cdde4f20")} className="c-picker-steps">
        <li className="c-picker-steps-current">
          <span aria-current="step" className="c-picker-steps-pill">
            <span className="c-picker-steps-mark">1</span>
            <span className="c-picker-steps-label">{portalText("operations", "template_3ec1ae06")}</span>
          </span>
        </li>
        <li aria-hidden="true" className="c-picker-steps-line" />
        <li className="c-picker-steps-next">{portalText("operations", "next_steps_depend_on_the_agent_you_choose_e301f514")}</li>
      </ol>
      <h1 className="c-screen-title">{portalText("operations", "which_agent_do_you_need_3a368986")}</h1>

      {[false, true].map((internal) => (
        <section key={String(internal)} className="c-picker-audience" aria-labelledby={internal ? "internal-agents" : "external-agents"}>
          <h2 id={internal ? "internal-agents" : "external-agents"} className="c-picker-section-title">{portalText("operations", internal ? "agent_audience_internal_group" : "agent_audience_external_group")}</h2>
          <p className="c-screen-lede">{portalText("operations", internal ? "agent_audience_internal_help" : "agent_audience_external_help")}</p>
          <div className="c-picker-grid">
            {catalogue.data.filter((card) => isInternalAgentTemplate(card.code) === internal).map((card) => (
              <button
                type="button"
                key={card.code}
                className="c-picker-card"
                disabled={!card.available}
                aria-describedby={card.available ? undefined : `${card.code}-availability`}
                onClick={() => {
                  void navigate({
                    to: "/agents/new/$agentTemplateCode",
                    params: { agentTemplateCode: card.code },
                    search: { step: 2 },
                  });
                }}
              >
                <span className="c-picker-card-head">
                  <span className="c-picker-card-mark">
                    <Icon glyph={glyphOf(card.iconName)} size="xl" />
                  </span>
                  {/* The 2026-10-10 export draws a badge only when one is set and sets none: an available card carries
                      none. The four that are not buildable say so (copy rule, 2026-09-23). */}
                  {card.available ? null : (
                    <span id={`${card.code}-availability`} className="c-picker-card-badge">
                      <Tag tone="accent">{portalText("operations", "coming_soon_e4115be2")}</Tag>
                    </span>
                  )}
                </span>
                <span className="c-picker-card-name">{card.name}</span>
                <span className="c-picker-card-dek">{card.dek}</span>
                <span className="c-picker-card-needs">{portalText("operations", "merge.wizard.you_ll_need.ebadd7")}{card.youAdd}</span>

                <span className="c-picker-card-direction">
                  <VoiceAgentAudienceTag agentTemplateCode={card.code} /> <Tag tone="neutral">{directionTagOf(card)}</Tag>
                </span>
              </button>
            ))}
          </div>
        </section>
      ))}

      <SharedConfigurationBlock catalogue={catalogue.data} />
    </section>
  );
}
